import { useMemo } from 'react';

import { useCommentQueryActions } from '@/api/hooks/commentQueryActions';
import { useEstimationItemAssumptionQueryActions } from '@/api/hooks/estimationItemAssumptionQueryActions';
import { useEstimationItemCostQueryActions } from '@/api/hooks/estimationItemCostQueryActions';
import { useEstimationItemQueryActions } from '@/api/hooks/estimationItemQueryActions';
import { useEstimationItemResourceQueryActions } from '@/api/hooks/estimationItemResourceQueryActions';
import { useEstimationModelQueryActions } from '@/api/hooks/estimationModelQueryActions';
import { useEstimationQueryActions } from '@/api/hooks/estimationQueryActions';
import { useMethodologyQueryActions } from '@/api/hooks/methodologyQueryActions';
import type {
  EstimationAdditionalFieldResponse,
  EstimationCommentResponse,
  EstimationItemAssumptionResponse,
  EstimationItemCostResponse,
  EstimationItemResourceResponse,
  EstimationItemResponse,
  EstimationModelResponse,
  EstimationResponse,
  MethodologyResponse,
  ProjectResponse,
  ProjectWithEstimationResponse,
} from '@/api/schema/data-contracts';

export interface EstimationWithDependencies {
  estimation: EstimationResponse;
  project: ProjectResponse;
  estimationModel: EstimationModelResponse;
  estimationItems: EstimationItemResponse[];
  methodologies: MethodologyResponse[];
  assumptions: EstimationItemAssumptionResponse[];
  comments: EstimationCommentResponse[];
  resources: EstimationItemResourceResponse[] | undefined;
  estimationItemCosts: EstimationItemCostResponse[] | undefined;
  additionalFields: EstimationAdditionalFieldResponse[] | undefined;
}

export interface UseGetEstimationWithDependenciesOptions {
  shouldFetchResources?: boolean;
  shouldFetchEstimationItemCosts?: boolean;
}

export const useGetEstimationWithDependencies = (
  estimationId: string,
  options?: UseGetEstimationWithDependenciesOptions,
): { isLoading: boolean; data: EstimationWithDependencies | undefined } => {
  const { useGetEstimation } = useEstimationQueryActions();
  const { data: estimationResponse, isLoading: isLoadingEstimation } = useGetEstimation(
    estimationId,
  ) as { data: ProjectWithEstimationResponse | undefined; isLoading: boolean };

  const { useGetEstimationModel: useGetEstimationModelDetail } = useEstimationModelQueryActions();
  const { data: estimationModel, isLoading: isLoadingEstimationModel } =
    useGetEstimationModelDetail(
      estimationResponse?.estimation.estimationModelId,
      estimationResponse?.estimation.cultureName,
    ) as { data: EstimationModelResponse | undefined; isLoading: boolean };

  const { useGetMethodologies } = useMethodologyQueryActions();
  const { data: methodologies, isLoading: isLoadingMethodologies } = useGetMethodologies(
    estimationResponse?.estimation.cultureName,
    {
      methodologyIds: estimationModel?.methodologyIds,
      onlyCurrent: false,
    },
    !!estimationModel && !!estimationResponse,
  ) as { data: MethodologyResponse[] | undefined; isLoading: boolean };

  const { useGetEstimationItemsByEstimationId } = useEstimationItemQueryActions();
  const { data: estimationItems, isLoading: isLoadingEstimationItems } =
    useGetEstimationItemsByEstimationId(estimationId);

  const { useGetEstimationItemAssumptionsByEstimationId } =
    useEstimationItemAssumptionQueryActions();

  const { useGetComments } = useCommentQueryActions();
  const { data: assumptions, isLoading: isLoadingAssumptions } =
    useGetEstimationItemAssumptionsByEstimationId(estimationId);

  const { data: comments, isLoading: isLoadingComments } = useGetComments(
    estimationId,
    'ESTIMATION',
  );

  const { useGetResourcesByEstimationId } = useEstimationItemResourceQueryActions();
  const { data: resources, isLoading: isLoadingEstimationResources } =
    useGetResourcesByEstimationId(options?.shouldFetchResources ? estimationId : undefined);

  const { useGetEstimationItemCostsByEstimationId } = useEstimationItemCostQueryActions();
  const { data: estimationItemCosts, isLoading: isLoadingEstimationItemCosts } =
    useGetEstimationItemCostsByEstimationId(
      options?.shouldFetchEstimationItemCosts ? estimationId : undefined,
    );

  const { useGetEstimationAdditionalFields } = useEstimationQueryActions();
  const { data: additionalFields } = useGetEstimationAdditionalFields(estimationId);

  const isLoading =
    isLoadingEstimation ||
    isLoadingEstimationItems ||
    isLoadingEstimationModel ||
    isLoadingMethodologies ||
    isLoadingAssumptions ||
    isLoadingComments ||
    (options?.shouldFetchResources && isLoadingEstimationResources) ||
    (options?.shouldFetchEstimationItemCosts && isLoadingEstimationItemCosts);

  const result = useMemo(() => {
    if (isLoading) {
      return {
        isLoading: true,
        data: undefined,
      };
    }

    if (
      estimationResponse === undefined ||
      estimationModel === undefined ||
      estimationItems === undefined ||
      methodologies === undefined ||
      assumptions === undefined ||
      comments === undefined ||
      (options?.shouldFetchResources && resources === undefined) ||
      (options?.shouldFetchEstimationItemCosts && estimationItemCosts === undefined)
    ) {
      return {
        isLoading: false,
        data: undefined,
      };
    }

    const resourceDisplayNames = estimationResponse.estimation.resourceDisplayNames;
    const estimationModelWithResourceDisplayNames =
      resourceDisplayNames == null
        ? estimationModel
        : {
            ...estimationModel,
            resourceTypes: estimationModel.resourceTypes.map((resourceType) => ({
              ...resourceType,
              name: resourceDisplayNames[resourceType.id] ?? resourceType.name,
            })),
          };

    return {
      isLoading: false,
      data: {
        estimation: estimationResponse.estimation,
        project: estimationResponse.project,
        estimationModel: estimationModelWithResourceDisplayNames,
        estimationItems,
        methodologies,
        assumptions,
        comments,
        resources,
        estimationItemCosts,
        additionalFields,
      },
    };
  }, [
    isLoading,
    estimationResponse,
    estimationModel,
    estimationItems,
    methodologies,
    assumptions,
    comments,
    resources,
    estimationItemCosts,
    additionalFields,
    options?.shouldFetchResources,
    options?.shouldFetchEstimationItemCosts,
  ]);

  return result;
};
